import { Tooltip } from 'antd/lib';
import { useState } from 'react';
import { CheckCircle, Clock, Plus, Trash2 } from 'react-feather';

import { Button } from '@/components';
import { SelectInput } from '@/components/select-input/SelectInput';
import { TextInput } from '@/components/text-input';
import { useTranslation } from 'react-i18next';
import { RequestBodyField, useAccountConnect } from '@/hooks/influencer/useAccountConnect';
import { useAccountDisconnect } from '@/hooks/influencer/useAccountDisconnect';
import { PlatformIcon } from '@/utils/platformIcon';
import { filteredPlatformsList } from '@/utils/platformsList';
import { DefaultOptionType } from 'antd/es/select';

import DisconnectAccountModal from '../modals/DisconnectAccountModal';
import styles from '../Settings.module.css';

interface SocialField {
  platform: DefaultOptionType | null;
  value: string;
}
interface AccountsSectionProps {
  onUpdate: (user: Api.UserObject) => void;
  user: Api.UserObject;
}

export function AccountsSection({ user, onUpdate }: AccountsSectionProps) {
  const [accounts, setAccounts] = useState<RequestBodyField[]>([]);
  const [removePopup, setRemovePopup] = useState<boolean>(false);
  const [accountField, setAccountField] = useState<boolean>(false);
  const [selectedPlatform, setSelectedPlatform] = useState<Api.SocialAccounts | null>(null);
  const [newAccount, setNewAccount] = useState<SocialField>({ platform: null, value: '' });

  const { t } = useTranslation();
  const { isLoading, mutate } = useAccountConnect({
    accounts,
    onSuccess: (res) => {
      if (res && (res as Api.UserObject).user_id) {
        setNewAccount({ platform: null, value: '' });
        onUpdate(res as Api.UserObject);
      }
    }
  });
  const { mutate: disconnectAccount, isLoading: isDisconnectiong } = useAccountDisconnect({
    platform: {
      platform: selectedPlatform?.platform,
      handle: selectedPlatform?.handle
    },
    onSuccess: (res) => {
      if (res && (res as Api.UserObject).user_id) {
        setRemovePopup(false);
        setSelectedPlatform(null);
        onUpdate(res as Api.UserObject);
      }
    }
  });

  function handleChange(val: DefaultOptionType | string) {
    if (typeof val === 'string') {
      setNewAccount({
        ...newAccount,
        value: val
      });
    } else {
      setNewAccount({
        ...newAccount,
        platform: val
      });
    }
  }

  function generateLabel(type: string): string | undefined {
    if (type === 'social_network') return t('inviteView.accountModal.username');
    if (type === 'blog') return t('inviteView.accountModal.link');
    return `${t('inviteView.accountModal.username')} / ${t('inviteView.accountModal.link')}`;
  }

  function handleRemove(platform: Api.SocialAccounts) {
    setSelectedPlatform(platform);
    setRemovePopup(true);
  }

  function handleSubmit() {
    if (!newAccount.platform) return;
    const { value, platform } = newAccount;

    const data: RequestBodyField = {
      platform: platform.value as string
    };
    if (platform.type === 'blog') {
      data.permalink = value.toString();
    }
    if (platform.type === 'social_network') {
      if (value.startsWith('@')) {
        data.handle = value;
      } else {
        data.handle = `@${value}`;
      }
    }

    setAccounts([data]);
    setTimeout(() => mutate(), 0);
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionInfo}>
        <div className={styles.sectionInfoTitle}>{t('account.accountsSection.title')}</div>
        <div className={styles.sectionInfoDescription}>
          {t(
            user.is_agent
              ? 'account.accountsSection.descriptionAgent'
              : 'account.accountsSection.description'
          )}
        </div>
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.sectionTable}>
          <div className={styles.row}>
            <div className={styles.grow}>
              <div>
                {user.influencer?.social_accounts.map((a) => (
                  <div key={`${a.platform}-${a.handle}`} className={styles.account}>
                    <div className={styles.accountBody}>
                      <span className={styles.accountBodyIcon}>{PlatformIcon(a.platform)}</span>
                      <span className={styles.accountBodyUsername}>{a.handle}</span>
                      <Tooltip
                        title={
                          a.is_verified
                            ? t('account.accountsSection.verified')
                            : t('account.accountsSection.pending')
                        }
                      >
                        {a.is_verified ? (
                          <CheckCircle size={16} style={{ color: '#7AD098' }} />
                        ) : (
                          <Clock size={16} style={{ color: '#474E5450' }} />
                        )}
                      </Tooltip>
                    </div>
                    {!a.is_verified && (
                      <div className={styles.accountIcon}>
                        <Trash2 color="red" size={20} onClick={() => handleRemove(a)} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {accountField && (
                <div className={`${styles.sectionBodyItem} ${styles.sectionAccountsBodyItem}`}>
                  <div className={styles.sectionAccountsFlex}>
                    <SelectInput
                      label={t('inviteView.accountModal.platform')}
                      value={newAccount.platform?.value}
                      options={filteredPlatformsList}
                      onChange={(val, objVal) => handleChange(objVal)}
                    />
                    <TextInput
                      label={generateLabel(newAccount.platform?.type)}
                      value={newAccount.value}
                      onChange={(e) => handleChange(e.target.value)}
                      disabled={!newAccount.platform}
                    />
                  </div>
                  <div className={styles.emailButtons}>
                    <Button
                      type="button"
                      isLoading={isLoading}
                      theme="cta"
                      style={{ padding: '5px 35px' }}
                      disabled={!newAccount.platform || !newAccount.value}
                      onClick={handleSubmit}
                    >
                      {t('account.add')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setAccountField(false)}
                      className={styles.emailButton}
                    >
                      {t('account.cancel')}
                    </Button>
                  </div>
                </div>
              )}
              <Button
                type="button"
                onClick={() => setAccountField(true)}
                disabled={accountField}
                className={`${styles.emailButton} ${styles.emailIconButton}`}
              >
                <Plus color={!accountField ? 'black' : '#474e5480'} />
                <span>{t('inviteView.accountModal.addButton')}</span>
              </Button>
            </div>
            <div className={styles.fitContent} />
          </div>
        </div>
      </div>
      {removePopup && selectedPlatform && (
        <DisconnectAccountModal
          onClose={() => setRemovePopup(false)}
          platform={{
            icon: PlatformIcon(selectedPlatform?.platform),
            name: selectedPlatform?.handle
          }}
          isLoading={isDisconnectiong}
          onNext={disconnectAccount}
        />
      )}
    </div>
  );
}
